// PC通知サービス（梅プラン専用機能）
export class PCNotificationService {
  constructor() {
    this.isSupported = 'Notification' in window
    this.permission = this.isSupported ? Notification.permission : 'denied'
    this.isEnabled = false
  }

  // 通知権限の要求
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('このブラウザは通知機能をサポートしていません')
    }

    if (this.permission === 'granted') {
      this.isEnabled = true
      return true
    }

    if (this.permission === 'denied') {
      throw new Error('通知が拒否されています。ブラウザ設定で通知を許可してください')
    }

    try {
      const permission = await Notification.requestPermission()
      this.permission = permission
      this.isEnabled = permission === 'granted'
      
      if (permission === 'granted') {
        // テスト通知
        this.showNotification('AI受付システム', {
          body: 'PC通知が有効になりました',
          icon: '/favicon.ico',
          tag: 'permission-granted'
        })
      }

      return this.isEnabled
    } catch (error) {
      console.error('通知権限要求エラー:', error)
      throw new Error('通知権限の要求中にエラーが発生しました')
    }
  }

  // 通知の表示
  showNotification(title, options = {}) {
    if (!this.isEnabled) {
      console.warn('PC通知が有効ではありません')
      return null
    }

    const defaultOptions = {
      body: '',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'alsok-notification',
      requireInteraction: false,
      silent: false,
      timestamp: Date.now(),
      renotify: false
    }

    const mergedOptions = { ...defaultOptions, ...options }

    try {
      const notification = new Notification(title, mergedOptions)

      // 自動で閉じる（デフォルト5秒）
      if (!mergedOptions.requireInteraction) {
        setTimeout(() => {
          notification.close()
        }, mergedOptions.autoClose || 5000)
      }

      // イベントハンドラー
      notification.onclick = () => {
        window.focus()
        if (mergedOptions.onClick) {
          mergedOptions.onClick()
        }
        notification.close()
      }

      notification.onerror = (error) => {
        console.error('通知エラー:', error)
      }

      return notification
    } catch (error) {
      console.error('通知表示エラー:', error)
      return null
    }
  }

  // 来訪者到着通知（梅プラン特化）
  notifyVisitorArrival(visitorData) {
    const title = '🏢 来訪者到着'
    const body = `${visitorData.name}様が到着されました`
    
    return this.showNotification(title, {
      body,
      icon: '/icons/visitor-arrival.png',
      tag: 'visitor-arrival',
      requireInteraction: true,
      onClick: () => {
        // 受付画面にフォーカス
        console.log('来訪者到着通知がクリックされました:', visitorData)
      }
    })
  }

  // 会議リマインダー通知
  notifyMeetingReminder(meetingData) {
    const title = '⏰ 会議リマインダー'
    const body = `${meetingData.title}が${meetingData.minutesBefore}分後に開始します`
    
    return this.showNotification(title, {
      body,
      icon: '/icons/meeting-reminder.png',
      tag: 'meeting-reminder',
      requireInteraction: false,
      autoClose: 8000
    })
  }

  // システム通知
  notifySystemAlert(alertData) {
    const title = '🚨 システム通知'
    const { message, severity = 'info' } = alertData
    
    const severityIcons = {
      error: '🔴',
      warning: '🟡',
      info: '🔵',
      success: '🟢'
    }

    return this.showNotification(title, {
      body: `${severityIcons[severity]} ${message}`,
      icon: '/icons/system-alert.png',
      tag: `system-${severity}`,
      requireInteraction: severity === 'error',
      autoClose: severity === 'error' ? 0 : 6000
    })
  }

  // 受付状態変更通知
  notifyReceptionStatusChange(statusData) {
    const title = '📍 受付状態変更'
    const { status, message } = statusData
    
    const statusIcons = {
      active: '✅',
      inactive: '❌',
      maintenance: '🔧'
    }

    return this.showNotification(title, {
      body: `${statusIcons[status]} ${message}`,
      icon: '/icons/reception-status.png',
      tag: 'reception-status',
      requireInteraction: false,
      autoClose: 4000
    })
  }

  // 音声認識結果通知（梅プラン専用）
  notifyVoiceRecognition(recognitionData) {
    const title = '🎤 音声認識'
    const { transcript, confidence } = recognitionData
    
    return this.showNotification(title, {
      body: `認識結果: "${transcript}" (信頼度: ${Math.round(confidence * 100)}%)`,
      icon: '/icons/voice-recognition.png',
      tag: 'voice-recognition',
      requireInteraction: false,
      autoClose: 3000
    })
  }

  // バッチ通知（複数の通知をまとめて）
  showBatchNotification(notifications) {
    if (notifications.length === 0) return

    if (notifications.length === 1) {
      const { title, options } = notifications[0]
      return this.showNotification(title, options)
    }

    // 複数の通知をまとめる
    const title = `📬 ${notifications.length}件の通知`
    const body = notifications.map(n => n.title).join('\n')

    return this.showNotification(title, {
      body,
      icon: '/icons/batch-notification.png',
      tag: 'batch-notification',
      requireInteraction: true,
      onClick: () => {
        // 通知履歴画面を開く
        console.log('バッチ通知がクリックされました')
      }
    })
  }

  // 通知の一括削除
  closeAllNotifications() {
    // 現在開いている通知を閉じる（ブラウザAPIの制限により、自分が作成した通知のみ）
    console.log('全ての通知を閉じています...')
  }

  // 通知設定の保存
  saveSettings(settings) {
    localStorage.setItem('alsok_notification_settings', JSON.stringify({
      enabled: settings.enabled,
      types: settings.types || ['visitor', 'meeting', 'system'],
      sound: settings.sound !== false,
      autoClose: settings.autoClose || 5000,
      savedAt: new Date().toISOString()
    }))
  }

  // 通知設定の読み込み
  loadSettings() {
    try {
      const saved = localStorage.getItem('alsok_notification_settings')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.warn('通知設定の読み込みに失敗:', error)
    }

    // デフォルト設定
    return {
      enabled: false,
      types: ['visitor', 'meeting', 'system'],
      sound: true,
      autoClose: 5000
    }
  }

  // 通知状態の取得
  getStatus() {
    return {
      isSupported: this.isSupported,
      permission: this.permission,
      isEnabled: this.isEnabled,
      settings: this.loadSettings()
    }
  }
}

// シングルトンインスタンス
const pcNotificationService = new PCNotificationService()

export default pcNotificationService

// 便利な関数をエクスポート
export const requestNotificationPermission = () => {
  return pcNotificationService.requestPermission()
}

export const showNotification = (title, options) => {
  return pcNotificationService.showNotification(title, options)
}

export const notifyVisitorArrival = (visitorData) => {
  return pcNotificationService.notifyVisitorArrival(visitorData)
}

export const notifyMeetingReminder = (meetingData) => {
  return pcNotificationService.notifyMeetingReminder(meetingData)
}

export const notifySystemAlert = (alertData) => {
  return pcNotificationService.notifySystemAlert(alertData)
}